import { useMutation } from '@apollo/client/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ChevronDownIcon,
  EyeIcon,
  EyeOffIcon,
  GripIcon,
  PenIcon,
  Trash2Icon
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { UPDATE_APPENDIX } from './constants';
import DeleteAppendixDialog from './DeleteAppendixDialog';
import type { Appendix, GetLayoutTypes } from './types';

import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

interface AppendixCardProps {
  appendix: Appendix;
  data: GetLayoutTypes;
  onSuccess: () => void;
}

function imageSrc(base64OrDataUrl: string): string {
  return base64OrDataUrl.startsWith('data:')
    ? base64OrDataUrl
    : `data:image/jpeg;base64,${base64OrDataUrl}`;
}

function stripHtml(html: string): string {
  if (!html) return '';
  const tmp = document.createElement('DIV');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
}

const AppendixCard = ({ appendix, data, onSuccess }: AppendixCardProps) => {
  const navigate = useNavigate();
  const { id: opportunityId } = useParams<{ id: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const isGeneral = appendix.appendixType === 'general';

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: appendix.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  const layoutTypeName =
    data.layoutTypes.find(lt => lt.value === appendix.layoutType)?.name ??
    appendix.layoutType;

  const [toggleHidden] = useMutation(UPDATE_APPENDIX, {
    onCompleted: () => {
      toast.success(
        appendix.hidden ? 'Appendix is now visible' : 'Appendix is now hidden'
      );
      onSuccess();
    },
    onError: () => {
      toast.error('Failed to update appendix visibility');
    }
  });

  const handleToggleHidden = () => {
    void toggleHidden({
      variables: {
        hidden: !appendix.hidden,
        id: appendix.id
      }
    });
  };

  const hasContentPreview =
    (appendix.image && appendix.image.length > 0) ||
    (appendix.text && appendix.text.length > 0);

  return (
    <>
      <Collapsible
        onOpenChange={setIsOpen}
        open={isOpen}
      >
        <div
          ref={setNodeRef}
          style={style}
          className={cn(
            'border-border bg-card rounded-md border',
            appendix.hidden && 'opacity-50',
            isDragging && 'z-50 shadow-lg'
          )}
        >
          <div className="flex items-center gap-3 px-5 py-3">
            <button
              className="text-muted-foreground cursor-grab touch-none active:cursor-grabbing"
              type="button"
              {...attributes}
              {...listeners}
            >
              <GripIcon className="size-5" />
            </button>

            <div className="flex min-w-0 flex-1 items-center gap-2">
              <span className="truncate font-medium">{appendix.title}</span>
              <span className="text-muted-foreground text-sm">
                {layoutTypeName}
              </span>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              <Button
                onClick={() =>
                  navigate(
                    `/investments/direct/${opportunityId}/appendices/${appendix.id}`
                  )
                }
                size="icon-sm"
                title="Edit"
                variant="smc-blue"
              >
                <PenIcon className="size-4" />
              </Button>

              <Button
                onClick={handleToggleHidden}
                size="icon-sm"
                title={appendix.hidden ? 'Show' : 'Hide'}
                variant="ghost"
              >
                {appendix.hidden ? (
                  <EyeOffIcon className="size-4" />
                ) : (
                  <EyeIcon className="size-4" />
                )}
              </Button>

              {!isGeneral && (
                <Button
                  onClick={() => setIsDeleteOpen(true)}
                  size="icon-sm"
                  title="Delete"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2Icon className="size-4" />
                </Button>
              )}

              <CollapsibleTrigger asChild>
                <Button
                  size="icon-sm"
                  variant="ghost"
                >
                  <ChevronDownIcon
                    className={cn(
                      'size-4 transition-transform',
                      isOpen && 'rotate-180'
                    )}
                  />
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>

          <CollapsibleContent>
            <div className="border-border space-y-3 border-t px-5 py-4">
              {(appendix.image || appendix.image2) && (
                <div className="flex gap-3">
                  {appendix.image && (
                    <img
                      alt={`${appendix.title} preview`}
                      className="max-h-40 rounded object-contain"
                      src={imageSrc(appendix.image)}
                    />
                  )}
                  {appendix.image2 && (
                    <img
                      alt={`${appendix.title} preview 2`}
                      className="max-h-40 rounded object-contain"
                      src={imageSrc(appendix.image2)}
                    />
                  )}
                </div>
              )}
              {[appendix.textHeader1, appendix.textHeader2, appendix.textHeader3].some(Boolean) && (
                <div className="flex gap-3">
                  {appendix.textHeader1 && (
                    <span className="text-sm font-medium">{appendix.textHeader1}</span>
                  )}
                  {appendix.textHeader2 && (
                    <span className="text-sm font-medium">{appendix.textHeader2}</span>
                  )}
                  {appendix.textHeader3 && (
                    <span className="text-sm font-medium">{appendix.textHeader3}</span>
                  )}
                </div>
              )}
              {[appendix.text, appendix.text2, appendix.text3, appendix.text4]
                .filter(Boolean)
                .map((txt, i) => (
                  <p
                    className="text-muted-foreground line-clamp-3 text-sm"
                    key={i}
                  >
                    {stripHtml(txt)}
                  </p>
                ))}
              {!appendix.image &&
                !appendix.image2 &&
                !appendix.text &&
                !appendix.text2 &&
                !appendix.text3 &&
                !appendix.text4 &&
                !appendix.textHeader1 &&
                !appendix.textHeader2 &&
                !appendix.textHeader3 && (
                  <p className="text-muted-foreground text-sm">
                    No content yet.
                  </p>
                )}
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>

      {!isGeneral && (
        <DeleteAppendixDialog
          appendixId={appendix.id}
          onClose={() => setIsDeleteOpen(false)}
          onSuccess={onSuccess}
          open={isDeleteOpen}
        />
      )}
    </>
  );
};

export default AppendixCard;
